const storageKey = 'asap:watchlist:v1';

export function cleanProduct(value) {
  if (!value || !/^\d+$/.test(String(value.id)) || typeof value.title !== 'string' || !value.title.trim()) return null;
  if (typeof value.url !== 'string' || !/^\/(?!\/)/.test(value.url) || /[\\\r\n]/.test(value.url)) return null;
  const url = new URL(value.url, 'https://watchlist.invalid');
  if (url.origin !== 'https://watchlist.invalid' || !url.pathname.includes('/products/')) return null;
  return { id: String(value.id), title: value.title.slice(0, 500), url: value.url, image: typeof value.image === 'string' ? value.image : '' };
}

export function createWatchlistStore(storage) {
  function read() {
    let value;
    try { value = JSON.parse(storage.getItem(storageKey) || '[]'); } catch (error) {
      if (error instanceof SyntaxError) return [];
      throw error;
    }
    if (!Array.isArray(value)) return [];
    const seen = new Set();
    return value.map(cleanProduct).filter(item => item && !seen.has(item.id) && seen.add(item.id));
  }
  return {
    read,
    toggle(value) {
      const product = cleanProduct(value);
      if (!product) throw new Error('Invalid product');
      const items = read();
      const saved = items.some(item => item.id === product.id);
      const next = saved ? items.filter(item => item.id !== product.id) : [...items, product];
      storage.setItem(storageKey, JSON.stringify(next));
      return { items: next, saved: !saved };
    },
  };
}

if (typeof document !== 'undefined' && !customElements.get('asap-watchlist-toggle')) {
  const toggles = new Set();
  let panel;
  let items = [];
  const store = createWatchlistStore({ getItem: key => localStorage.getItem(key), setItem: (key, value) => localStorage.setItem(key, value) });

  function refresh() {
    try { items = store.read(); } catch { items = []; }
    toggles.forEach(toggle => toggle.update());
    panel?.update();
  }

  function change(product) {
    try {
      const result = store.toggle(product);
      items = result.items;
      toggles.forEach(toggle => toggle.update());
      panel?.update();
      panel?.message(`${product.title}: ${result.saved ? panel.dataset.added : panel.dataset.removed}`);
    } catch {
      panel?.message(panel.dataset.error, true);
    }
  }

  class WatchlistToggle extends HTMLElement {
    connectedCallback() {
      if (toggles.has(this)) return;
      toggles.add(this);
      this.button = this.querySelector('button');
      this.label = this.querySelector('[data-watchlist-label]');
      this.onClick = () => change(this.dataset);
      this.button.addEventListener('click', this.onClick);
      this.update();
      this.hidden = false;
    }
    disconnectedCallback() {
      toggles.delete(this);
      this.button?.removeEventListener('click', this.onClick);
    }
    update() {
      const saved = items.some(item => item.id === this.dataset.id);
      this.button.setAttribute('aria-pressed', String(saved));
      this.button.setAttribute('aria-label', `${saved ? this.dataset.remove : this.dataset.add}: ${this.dataset.title}`);
      this.label.textContent = saved ? this.dataset.remove : this.dataset.add;
    }
  }

  class Watchlist extends HTMLElement {
    connectedCallback() {
      if (panel === this) return;
      panel = this;
      this.dialog = this.querySelector('dialog');
      this.list = this.querySelector('[data-watchlist-items]');
      this.empty = this.querySelector('[data-watchlist-empty]');
      this.count = this.querySelector('[data-watchlist-count]');
      this.status = this.querySelector('[data-watchlist-status]');
      this.closeButton = this.querySelector('[data-watchlist-close]');
      this.querySelector('[data-watchlist-open]').addEventListener('click', () => {
        refresh();
        if (!this.dialog.open) this.dialog.showModal();
        this.update();
      });
      this.closeButton.addEventListener('click', () => this.dialog.close());
      this.list.addEventListener('click', event => {
        const button = event.target.closest('[data-watchlist-remove]');
        if (!button) return;
        const index = items.findIndex(item => item.id === button.dataset.watchlistRemove);
        const product = items[index];
        if (!product) return;
        change(product);
        const buttons = [...this.list.querySelectorAll('[data-watchlist-remove]')];
        (buttons[Math.min(index, buttons.length - 1)] || this.closeButton).focus();
      });
      this.hidden = false;
      refresh();
    }
    message(text, error = false) {
      this.status.textContent = text;
      this.status.classList.toggle('visually-hidden', !error);
      this.status.classList.toggle('asap-watchlist__error', error);
      this.querySelector('[data-watchlist-error]').textContent = error ? text : '';
    }
    update() {
      this.count.textContent = String(items.length);
      if (!this.dialog.open) return;
      this.empty.hidden = items.length > 0;
      const fragment = document.createDocumentFragment();
      for (const product of items) {
        const row = document.createElement('li');
        row.className = 'asap-watchlist__item';
        const image = document.createElement('img');
        image.className = 'asap-watchlist__image';
        image.alt = '';
        image.width = 72;
        image.height = 72;
        image.loading = 'lazy';
        try {
          const source = new URL(product.image, location.origin);
          if (source.protocol === 'https:' && [location.hostname, 'cdn.shopify.com'].includes(source.hostname)) image.src = source.href;
        } catch {}
        if (!image.hasAttribute('src')) image.hidden = true;
        const link = document.createElement('a');
        link.className = 'asap-watchlist__link';
        link.href = product.url;
        link.textContent = product.title;
        const remove = document.createElement('button');
        remove.type = 'button';
        remove.className = 'asap-watchlist__remove';
        remove.dataset.watchlistRemove = product.id;
        remove.textContent = this.dataset.remove;
        remove.setAttribute('aria-label', `${this.dataset.remove}: ${product.title}`);
        row.append(image, link, remove);
        fragment.append(row);
      }
      this.list.replaceChildren(fragment);
    }
  }

  customElements.define('asap-watchlist-toggle', WatchlistToggle);
  customElements.define('asap-watchlist', Watchlist);
  window.addEventListener('storage', event => { if (event.key === storageKey || event.key === null) refresh(); });
  window.addEventListener('pageshow', refresh);
}
